import { useEffect, useRef, useState } from 'react'

type TUseInViewOptions = {
    triggerOnce?: boolean
    rootMargin?: string
    threshold?: number
}

/**
 * Minimal IntersectionObserver hook (replaces audio-web's react-intersection-observer
 * dependency). Attach the returned `ref` to the tracked element; `inView` flips true when
 * it intersects the viewport. `triggerOnce` stops observing after the first intersection.
 */
export const useInView = <T extends Element = HTMLDivElement>({
    triggerOnce = false,
    rootMargin,
    threshold,
}: TUseInViewOptions = {}) => {
    const ref = useRef<T | null>(null)
    const [inView, setInView] = useState(false)

    useEffect(() => {
        const element = ref.current
        if (!element) {
            return undefined
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                setInView(entry.isIntersecting)
                if (entry.isIntersecting && triggerOnce) {
                    observer.disconnect()
                }
            },
            { rootMargin, threshold },
        )

        observer.observe(element)

        return () => observer.disconnect()
    }, [triggerOnce, rootMargin, threshold])

    return { ref, inView }
}
